import { Flex, Text } from '@chakra-ui/react'
import { Icon } from '@chakra-ui/react'
import { ContactRound, Sparkles } from 'lucide-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ExtensionBadgeVariant } from '~/components/shared/componentStyles/ExtensionBadge.style'
import { ExtensionBadge } from '~/components/shared/ExtensionBadge'
import { Tooltip } from '~/components/ui/tooltip'
import { useContactCellStyles } from '~/pages/Calls/componentStyles/CallsListItem.style'
import { ContactDetails } from '~/pages/Contacts/viewModels/Contacts.viewmodel'
import { Size } from '~/types/ThemeTypes'

interface ContactCellProps {
  columnStyles: Record<string, any>
  fromTitle: string
  toTitle: string
  fromSubtitle: string
  toSubtitle: string
  fromContact?: ContactDetails
  toContact?: ContactDetails
  fromNumber: string
  toNumber: string
  isFromExtension?: boolean
  isToExtension?: boolean
  transcriptionStatus?: string
  isFromBot?: boolean
  isToBot?: boolean
}
export const ContactCombinedCell = ({
  columnStyles,
  fromTitle,
  toTitle,
  fromContact,
  toContact,
  fromNumber,
  toNumber,
  fromSubtitle,
  toSubtitle,
  isFromExtension,
  isToExtension,
  isFromBot,
  isToBot,
}: ContactCellProps) => {
  const { t } = useTranslation()
  const styles = useContactCellStyles()

  const fromTooltipLabel = useMemo(() => {
    return (
      <Flex paddingY="0.25rem" gap="0.5rem" alignItems="center">
        <Text css={{ ...styles.subtitleText }} color="inherit">
          {fromSubtitle}
        </Text>
        {isFromExtension && (
          <ExtensionBadge
            extension={fromNumber}
            variant={ExtensionBadgeVariant.White}
            size={Size.sm}
          />
        )}
      </Flex>
    )
  }, [fromNumber, isFromExtension, fromSubtitle])

  const toTooltipLabel = useMemo(() => {
    return (
      <Flex paddingY="0.25rem" gap="0.5rem" alignItems="center">
        <Text css={{ ...styles.subtitleText }} color="inherit">
          {toSubtitle}
        </Text>
        {isToExtension && (
          <ExtensionBadge
            extension={toNumber}
            variant={ExtensionBadgeVariant.White}
            size={Size.sm}
          />
        )}
      </Flex>
    )
  }, [toNumber, isToExtension, toSubtitle])

  return (
    <Flex role="cell" css={{ ...columnStyles, ...styles.container }}>
      <Flex css={styles.titleContainer}>
        <Tooltip
          showArrow
          positioning={{ placement: 'top-start' }}
          content={fromTooltipLabel}
        >
          <Flex gap="0.5rem" alignItems="center" maxWidth="100%">
            <Text css={styles.titlePrefix}>
              {t('contactCombinedCell.fromPrefix', 'F:')}
            </Text>
            {isFromBot && (
              <Icon css={styles.titleIcon}>
                <Sparkles size="100%" />
              </Icon>
            )}
            {fromContact && !isFromBot && (
              <Icon css={styles.titleIcon}>
                <ContactRound size="100%" />
              </Icon>
            )}

            <Text css={styles.titleText}>{fromTitle}</Text>
          </Flex>
        </Tooltip>
      </Flex>
      <Flex css={styles.titleContainer}>
        <Tooltip
          showArrow
          positioning={{ placement: 'top-start' }}
          content={toTooltipLabel}
        >
          <Flex gap="0.5rem" alignItems="center" maxWidth="100%">
            <Text css={styles.titlePrefix}>
              {t('contactCombinedCell.toPrefix', 'T:')}
            </Text>
            {isToBot && (
              <Icon css={styles.titleIcon}>
                <Sparkles size="100%" />
              </Icon>
            )}
            {toContact && !isToBot && (
              <Icon css={styles.titleIcon}>
                <ContactRound size="100%" />
              </Icon>
            )}
            <Text css={styles.titleText}>{toTitle}</Text>
          </Flex>
        </Tooltip>
      </Flex>
    </Flex>
  )
}
